import React, { useState } from 'react';
import OurLink from '../../elements/OurLink';
import { getDateAndTime } from '../../../lib/utils';
import {
  Circle,
  Delete,
  ExpandMore,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  OpenInNew,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import { deleteExecution } from '../../../modules/executions';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { updateTestCaseAssociations } from '../../../modules/testcycles';
import { showUserDisplayName } from '../../../lib/userUtils';
import ActionButton from '../../../ui/primitives/ActionButton';
import CustomAvatar from '../../elements/CustomAvatar';
import { tokens } from '../../../mui-theme';
import { useUser } from '../../../context/UserContext';
import { Guard } from '../../../features/Guard';

const PILL_RADIUS = '6px';

const TestExecutionAccordionHeader = ({
  execution,
  data,
  statuses,
  selectedStatus,
  defaultStatus,
  onDropdownChange,
  timer,
  elapsedTime,
  onTimerClick,
  formatElapsedTime,
  fetchSelectedExecutionDetails,
  currentExecution,
  addNotification,
  executionId,
  singlePage,
  isExpanded,
  isTestCycleCompleted,
  fetchExecutionData,
  users,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAssignWarning, setShowAssignWarning] = useState(false);
  const [assignWarningMessage, setAssignWarningMessage] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);

  const { currentUser } = useUser();
  const association = execution?.association || currentExecution?.association;
  const assignedTo = execution?.executed_by || currentExecution?.executed_by;
  const isAssignedToMe = assignedTo === currentUser?.id;

  const isExecutionEditable =
    data?.status_details?.name === 'Approved' &&
    (execution?.is_editable || currentExecution?.is_editable);

  const currentStatus =
    statuses?.find((status) => status.id === selectedStatus) || defaultStatus;

  const assignedUserName = String(showUserDisplayName(assignedTo, users) ?? '');
  const nameParts = assignedUserName.trim().split(' ');
  const avatarFirst = nameParts[0] || '';
  const avatarLast = nameParts[1] || '';

  const handleDeleteExecution = async () => {
    try {
      await deleteExecution(execution?.id || executionId);
      addNotification('success', 'Execution deleted successfully!');
      setShowConfirm(false);
      fetchExecutionData();
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Failed to delete execution. Please try again.'
      );
    }
  };

  const handleAssignToMe = async (skip = false) => {
    try {
      const response = await updateTestCaseAssociations(association?.id, executionId, skip);
      if (response?.warning) {
        setAssignWarningMessage(response.message);
        setShowAssignWarning(true);
        return;
      }
      if (!singlePage) {
        addNotification('success', 'Test case assigned to you successfully!');
        fetchSelectedExecutionDetails();
        fetchExecutionData(1, true);
      } else {
        window.location.reload();
      }
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to assign.');
    }
  };

  const handleConfirmAssign = () => {
    setShowAssignWarning(false);
    handleAssignToMe(true);
  };

  const openNewTab = () => {
    const url = `/project/${data?.gainss_project_id}/testexecution/${currentExecution?.key}?testcycle=${currentExecution?.testcycle?.key}&association=${data?.key}`;
    window.open(url, '_blank');
  };

  const renderStatusDropdown = () => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'relative' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={(e) => {
            e.stopPropagation();
            setStatusOpen(true);
          }}
          sx={{
            backgroundColor: currentStatus?.color || '#6c757d',
            px: 1.5,
            py: 0.625,
            borderRadius: PILL_RADIUS,
            cursor: 'pointer',
            minWidth: 130,
            userSelect: 'none',
            '&:hover': { filter: 'brightness(0.9)' },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#fff',
              flexGrow: 1,
              lineHeight: 1.5,
            }}
          >
            {currentStatus?.name || 'Select Status'}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Stack>

        <Select
          open={statusOpen}
          onClose={() => setStatusOpen(false)}
          value={selectedStatus || ''}
          onChange={(e) => {
            e.stopPropagation();
            onDropdownChange(e.target.value);
          }}
          sx={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: 'none',
          }}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
        >
          {statuses?.map((status) => (
            <MenuItem
              key={status.id}
              value={status.id}
              onClick={(e) => e.stopPropagation()}
            >
              <Circle sx={{ color: status?.color, mr: 1, fontSize: 14 }} />
              {status?.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: PILL_RADIUS,
          px: 1.25,
          py: 0.625,
          minWidth: 115,
          backgroundColor: 'background.paper',
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTimerClick();
          }}
          aria-label={timer ? 'Stop Timer' : 'Start Timer'}
          sx={{ p: 0, color: timer ? 'error.main' : 'text.secondary' }}
        >
          {timer ? <Pause sx={{ fontSize: 18 }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
        </IconButton>
        <Typography
          variant="body2"
          sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: 'text.primary' }}
        >
          {formatElapsedTime(elapsedTime)}
        </Typography>
      </Stack>
    </Stack>
  );

  const renderReadOnlyActions = () => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Guard policy="ManageTestExecutions">
      {!isAssignedToMe && isExecutionEditable && (
        <ActionButton
          intent="secondary"
          size="small"
          label="Assign to me"
          onClick={(e) => {
            if (isExpanded) e.stopPropagation();
            handleAssignToMe();
          }}
          tooltip="Assign this execution to me"
        />
      )}
      </Guard>
      <Box
        sx={{
          backgroundColor:
            currentExecution?.status?.color || execution?.status?.color || '#6c757d',
          color: '#fff',
          px: 1.5,
          py: 0.625,
          borderRadius: PILL_RADIUS,
          fontSize: '0.8125rem',
          fontWeight: 600,
          textAlign: 'center',
          minWidth: 110,
          lineHeight: 1.5,
        }}
      >
        {currentExecution?.status?.name || execution?.status?.name || ''}
      </Box>
    </Stack>
  );

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', px: 2, py: 1.5, minHeight: 64 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {!singlePage && (
            <ExpandMore
              sx={{
                fontSize: 20,
                color: 'text.secondary',
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                flexShrink: 0,
              }}
            />
          )}

          {!singlePage && (
            <Tooltip title="Open execution in new tab" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openNewTab();
                }}
              >
                <OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Box>
            <OurLink to={`/project/${data?.gainss_project_id}/testcases/${data?.key}`}>
              <strong>{data?.key}</strong>
              {data?.module_details?.name ? ` - ${data.module_details.name}` : ''}
            </OurLink>
            <Typography
              variant="body1"
              color="text.primary"
              title={data?.summary}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 360,
              }}
            >
              {data?.summary}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Executed On:{' '}
              {getDateAndTime(execution?.updated_at || currentExecution?.updated_at)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CustomAvatar
            firstName={avatarFirst}
            lastName={avatarLast}
            size={32}
            showTitle
            backgroundColor={tokens.colors.neutral[400]}
            fallback={assignedUserName?.[0] || 'U'}
          />

          {isExecutionEditable && isAssignedToMe
            ? renderStatusDropdown()
            : renderReadOnlyActions()}

          {!singlePage && !isTestCycleCompleted && (
            <Guard policy="DeleteTestExecutions">
            <Tooltip title="Remove execution" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirm(true);
                }}
                sx={{
                  color: 'error.main',
                  border: '1px solid',
                  borderColor: 'error.light',
                  borderRadius: PILL_RADIUS,
                  '&:hover': { backgroundColor: 'error.light' },
                }}
              >
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            </Guard>
          )}
        </Stack>
      </Stack>

      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleDeleteExecution}
        handleClose={() => setShowConfirm(false)}
        title="Remove Execution"
        body={
          <Typography component="span" color="error">
            Are you sure you want to remove execution for <strong>{data?.key}</strong> from
            this iteration? This action cannot be undone.
          </Typography>
        }
      />
      <ConfirmationModal
        show={showAssignWarning}
        handleConfirm={handleConfirmAssign}
        handleClose={() => setShowAssignWarning(false)}
        title="Confirm Assignment"
        body={assignWarningMessage}
        buttonLabel="Assign Anyway"
        buttonStyle="primary"
      />
    </>
  );
};

export default TestExecutionAccordionHeader;